/**
 * Stories implemented
 */
/* eslint-disable local-rules/require-storybook-documentation */

/**
 * @stories {@link file://./../../stories/Experiences/CallbackSchedulerFailure.stories.tsx}
 */

import { type FC, type ReactNode } from 'react';
import { Box } from '@zillow/constellation-10';
import { css } from '@/styled-system/css';
import { applyConstellationScalingFactor } from 'src/utils/applyConstellationScalingFactor';
import { setCypressID } from 'src/utils/setCypressID';
import { CYPRESS_INTEGRATION_TEST_IDS } from 'src/common/constants';
import { PageLayout } from 'src/components/Layout/PageLayout';
import { constrainedWidthSpacer } from './styles/constrainedWidthSpacer.styles';

const DESIGN_SPECIFIED_SETTINGS = {
    HERO_SECTION_SPACING_PX: applyConstellationScalingFactor(20),
    BELOW_THE_FOLD_SPACING_PX: applyConstellationScalingFactor(48),
};

type HeroContentProps = {
    welcomeBannerModule: ReactNode;
    resourceModule: ReactNode;
    teamInformationModule: ReactNode;
    viewSavedInformationModule: ReactNode;
    heroGraphicModule: ReactNode;
    upsellModule: ReactNode;
};

const heroStyles = css({
    marginTop: 'sm',
    marginBottom: 'sm',
    '@media (max-width: 650px)': {
        order: 1,
    },
});

const containerStyles = css({
    marginTop: 'sm',
    marginBottom: `${DESIGN_SPECIFIED_SETTINGS.BELOW_THE_FOLD_SPACING_PX}px`,
    paddingLeft: {
        base: 0,
        smToXl: '{sizes.400}',
    },
    paddingRight: {
        base: 0,
        smToXl: '{sizes.400}',
    },
});

const gridContainerStyles = css({
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gridTemplateRows: 'auto 1fr',
    columnGap: 'xl',
    '@media (max-width: 768px)': {
        columnGap: 'sm',
    },
    '@media (max-width: 650px)': {
        gridTemplateColumns: '1fr',
        gridTemplateRows: 'auto auto auto',
    },
});

const resourceModuleStyles = css({
    '@media (max-width: 650px)': {
        order: 3,
    },
});

const teamInfoModuleStyles = css({
    gridRow: 'span 2',
    '@media (max-width: 650px)': {
        order: 4,
        gridRow: 'auto',
    },
});

const savedInfoModuleStyles = css({
    '@media (max-width: 650px)': {
        order: 5,
    },
});

export const DashboardPageLayout: FC<HeroContentProps> = ({
    welcomeBannerModule,
    resourceModule,
    teamInformationModule,
    viewSavedInformationModule,
    heroGraphicModule,
    upsellModule,
}) => (
    <PageLayout>
        {welcomeBannerModule}
        <Box className={containerStyles} {...setCypressID(CYPRESS_INTEGRATION_TEST_IDS.MORTGAGE_DASHBOARD.PAGE)}>
            <Box className={gridContainerStyles}>
                <Box className={constrainedWidthSpacer()}>{upsellModule}</Box>
                <Box className={heroStyles}>{heroGraphicModule}</Box>
                <Box className={resourceModuleStyles}>{resourceModule}</Box>
                <Box className={teamInfoModuleStyles}>{teamInformationModule}</Box>
                <Box className={savedInfoModuleStyles}>{viewSavedInformationModule}</Box>
            </Box>
        </Box>
    </PageLayout>
);
